import React, { useEffect, useMemo, useReducer, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, Circle, ChevronDown, ChevronUp, ListChecks, ArrowRight, Sparkles, FastForward } from "lucide-react";
import { getParticipantId, hasFeedbackSubmitted } from "@/lib/userInterviews";
import { useAuth } from "@/lib/auth";
/**
 * Floating, collapsible "guided study" task list for User Interviews testers.
 *
 * Only renders when an `iid` is in storage (i.e. the caregiver landed via
 * /tester/?iid=…). Hidden for everyone else so it never shows up in the
 * private beta or YC demo.
 *
 * Each task auto-checks off when the tester visits its route. Feedback
 * completion auto-checks when Feedback.jsx calls markFeedbackSubmitted.
 * Progress is persisted in localStorage keyed by iid so it survives refresh.
 */

const TASKS = [
    {
        id: "intake",
        label: "Peek at the Patient profile",
        to: "/patient",
        match: ["/patient"],
        time: "1 min",
        hint: "Just open it and look around. No need to save anything — this is the one-page sheet a paramedic can read in 10 seconds.",
    },
    {
        id: "roadmap",
        label: "See where you are on the journey",
        to: "/roadmap",
        match: ["/roadmap"],
        time: "1 min",
        hint: "The Roadmap is organized by life area — medical, legal, daily care, end-of-life. Each recommendation has compassionate statuses like \"Already in place\" or \"Working on it.\" Toggle a couple to see how it follows along — no checklist pressure.",
    },
    {
        id: "lane",
        label: "Talk to Lane if you'd like",
        to: "/chat",
        match: ["/chat"],
        time: "1–2 min",
        hint: "Try a question if something comes to mind — anything from \"sundowning\" to \"I'm tired.\" Or just read Lane's opening.",
    },
    {
        id: "learn",
        label: "Browse the Knowledge Library",
        to: "/education#knowledge-library",
        match: ["/education", "/library"],
        time: "1–2 min",
        hint: "Scroll to the Knowledge Library section — 90+ guides grouped by topic. Try the search bar (e.g. \"sundowning\", \"Medicare\") or open one article that catches your eye. A paragraph is plenty.",
    },
    {
        id: "behavior",
        label: "Peek at the Behavior tracker",
        to: "/behavior",
        match: ["/behavior"],
        time: "30 sec",
        hint: "Just open it. Try logging something only if it feels natural — nothing has to be saved.",
    },
    {
        id: "guidance",
        label: "Try Behavior guidance",
        to: "/guidance",
        match: ["/guidance"],
        time: "1 min",
        hint: "Pick a situation that resonates and read what comes back. No commitment.",
    },
    {
        id: "hardtalks",
        label: "Look at a hard conversation script",
        to: "/conversations",
        match: ["/conversations"],
        time: "1 min",
        hint: "Driving, moving in, hospice, money — open one and read the draft.",
    },
    {
        id: "resources",
        label: "Find help in your state",
        to: "/help-finder",
        match: ["/help-finder"],
        time: "30 sec",
        hint: "Pick a state, browse a resource. Reading is enough.",
    },
    {
        id: "carenetwork",
        label: "Peek at the Care network",
        to: "/care-network",
        match: ["/care-network"],
        time: "30 sec",
        hint: "Just look at what kind of in-person help we'd connect families with.",
    },
    {
        id: "appointment",
        label: "Peek at Appointment prep",
        to: "/appointment",
        match: ["/appointment"],
        time: "30 sec",
        hint: "Open it and skim. You don't need to add or save anything.",
    },
    {
        id: "navigator",
        label: "Peek at the Care navigator",
        to: "/navigator",
        match: ["/navigator"],
        time: "30 sec",
        hint: "Just read the page — see if a 1:1 navigator would help.",
    },
    {
        id: "family",
        label: "See how to pull your family in",
        to: "/family",
        match: ["/family"],
        time: "30 sec",
        hint: "Look at the share-situation tool. No need to send anything.",
    },
    {
        id: "burnout",
        label: "Peek at the burnout check-in",
        to: "/burnout",
        match: ["/burnout"],
        time: "1 min",
        hint: "Open it. Take the 6 questions only if you want to — otherwise just look.",
    },
    {
        id: "feedback",
        label: "Share what you learned",
        to: "/feedback",
        match: ["/feedback"],
        time: "3 min",
        hint: "The 8-question survey. This is the only step we really need from you.",
    },
];

export default function TesterTaskPanel() {
    const location = useLocation();
    const { user } = useAuth();
    const iid = getParticipantId();
    // Strictly tester-only: must be logged in as a tester account (only
    // accounts created via /tester/:code or /tester/?iid=… have a tester_code)
    // AND must currently have an iid in storage. This protects against a
    // founder/admin who self-tested via /tester/?iid=… once and now sees the
    // panel on every login even when signed in as themselves.
    const isTesterUser = !!(user && user !== false && user.role !== "admin" && user.tester_code);
    const storageKey = iid && isTesterUser ? `mlcn_tester_tasks_${iid}` : "";

    // Belt-and-suspenders: if we have an iid in storage but the logged-in user
    // is NOT a tester, clear the stale id so it can't leak any other tester
    // UX (e.g. the Finish-study button). user === false means "not logged in"
    // — we only clear when we definitively know it's a non-tester real user.
    useEffect(() => {
        if (!iid) return;
        if (user && user !== false && !user.tester_code) {
            try {
                sessionStorage.removeItem("mlcn_participant_id");
                localStorage.removeItem("mlcn_participant_id");
            } catch { /* noop */ }
        }
    }, [iid, user]);

    // Prevent testers from closing the tab / hitting back / reloading away
    // without finishing the survey. The browser will show its native
    // "Leave site? Changes you made may not be saved" prompt. Stops firing
    // the moment they submit feedback (hasFeedbackSubmitted is true).
    useEffect(() => {
        if (!isTesterUser) return;
        if (hasFeedbackSubmitted(iid)) return;
        const onBeforeUnload = (e) => {
            // Modern browsers ignore the custom message and show their own,
            // but we still need to set returnValue + preventDefault to trigger it.
            e.preventDefault();
            e.returnValue = "You haven't finished the 3-min survey yet — please complete it before you go.";
            return e.returnValue;
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [isTesterUser, iid]);

    const [open, setOpen] = useState(() => {
        try { return localStorage.getItem(`${storageKey}_open`) !== "0"; } catch (_e) { return true; }
    });

    // useReducer instead of useState for `done` — it's the React-19-blessed
    // way to merge state from an effect (the set-state-in-effect rule allows
    // dispatch but not setState directly). The reducer also persists to
    // localStorage in the same atomic step.
    const [done, dispatchDone] = useReducer(
        (state, action) => {
            if (action.type === "mark") {
                if (state[action.id]) return state;
                const next = { ...state, [action.id]: true };
                try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch (_e) { /* noop */ }
                return next;
            }
            return state;
        },
        null,
        () => {
            if (!storageKey) return {};
            try { return JSON.parse(localStorage.getItem(storageKey) || "{}"); } catch (_e) { return {}; }
        },
    );

    // Auto-check the matching task whenever the route changes.
    useEffect(() => {
        if (!storageKey) return;
        const path = location.pathname;
        const hit = TASKS.find(t => t.match.some(m => path.startsWith(m)));
        if (hit) dispatchDone({ type: "mark", id: hit.id });
        if (hasFeedbackSubmitted(iid)) dispatchDone({ type: "mark", id: "feedback" });
    }, [location.pathname, storageKey, iid]);

    const total = TASKS.length;
    const completed = useMemo(() => TASKS.filter(t => done[t.id]).length, [done]);
    const pct = Math.round((completed / total) * 100);
    // "Halfway there" celebration trips exactly once when the tester
    // crosses 50% and they haven't yet submitted feedback. Stored per-iid
    // so it doesn't re-appear on refresh — the celebration is a moment,
    // not a sticky banner.
    const halfwayKey = storageKey ? `${storageKey}_halfway_seen` : "";
    const [showHalfway, setShowHalfway] = useState(false);
    useEffect(() => {
        if (!halfwayKey) return;
        if (hasFeedbackSubmitted(iid)) return;
        const alreadySeen = (() => {
            try { return localStorage.getItem(halfwayKey) === "1"; } catch { return false; }
        })();
        if (alreadySeen) return;
        if (completed >= Math.ceil(total / 2) && completed < total) {
            setShowHalfway(true);
            try { localStorage.setItem(halfwayKey, "1"); } catch { /* noop */ }
        }
    }, [completed, total, halfwayKey, iid]);

    if (!iid || !isTesterUser) return null;

    const toggle = () => {
        const next = !open;
        setOpen(next);
        try { localStorage.setItem(`${storageKey}_open`, next ? "1" : "0"); } catch (_e) { /* noop */ }
    };

    return (
        <aside
            data-testid="tester-task-panel"
            className="fixed bottom-[84px] md:bottom-4 right-4 z-40 w-[min(320px,calc(100vw-2rem))] sm:w-[360px] no-print pointer-events-none"
        >
            <div className="bg-white rounded-3xl shadow-xl border border-stage_late-light overflow-hidden pointer-events-auto">
                <button
                    type="button"
                    onClick={toggle}
                    data-testid="tester-task-toggle"
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-stage_late-light hover:bg-stage_late-light/80 text-stage_late-dark"
                >
                    <span className="flex items-center gap-2 text-sm font-heading font-medium">
                        <ListChecks size={16} strokeWidth={1.7} />
                        Your study tasks
                        <span className="text-xs tabular-nums opacity-70">{completed}/{total}</span>
                    </span>
                    {open ? <ChevronDown size={16} strokeWidth={1.7} /> : <ChevronUp size={16} strokeWidth={1.7} />}
                </button>

                {/* Progress bar always visible */}
                <div className="h-1 bg-cream-dark">
                    <div
                        className="h-full bg-stage_late-dark transition-all"
                        style={{ width: `${pct}%` }}
                        data-testid="tester-task-progress"
                    />
                </div>

                {open && (
                    <div className="p-4 space-y-2.5 max-h-[60vh] overflow-y-auto">
                        <p className="text-xs text-[#4C5551] leading-relaxed">
                            This is a tour. <strong>Open each section and try anything that interests you</strong> — you don&apos;t need to finish or save anything along the way. The only step we really need from you is the <strong>8-question survey at the end</strong>. About 10–15 minutes total if you move briskly, but stay as long as you like.
                        </p>

                        {/* Halfway-there celebration — appears exactly once when
                            the tester crosses 50% of the tour. Keeps the
                            momentum without nagging. */}
                        {showHalfway && !hasFeedbackSubmitted(iid) && (
                            <div
                                data-testid="tester-task-halfway-banner"
                                className="rounded-2xl bg-teal-soft/80 border border-teal-soft p-3 flex items-start gap-2"
                            >
                                <Sparkles size={16} strokeWidth={1.8} className="text-teal-dark shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-sm font-heading font-medium text-teal-dark leading-snug">
                                        You&apos;ve explored {completed} of {total} sections — halfway there.
                                    </p>
                                    <p className="text-xs text-[#4C5551] mt-0.5 leading-snug">
                                        If you&apos;re short on time, you can skip straight to the survey now — your impressions so far are exactly what we need.
                                    </p>
                                    <Link
                                        to="/feedback"
                                        data-testid="tester-task-halfway-skip"
                                        className="inline-flex items-center gap-1 mt-1.5 text-xs font-semibold text-teal-dark hover:underline"
                                    >
                                        Skip to the survey <ArrowRight size={12} strokeWidth={2} />
                                    </Link>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowHalfway(false)}
                                    aria-label="Dismiss halfway message"
                                    data-testid="tester-task-halfway-dismiss"
                                    className="text-[#6E7A74] hover:text-[#4C5551] -m-1 p-1"
                                >
                                    <ChevronUp size={14} strokeWidth={2} />
                                </button>
                            </div>
                        )}

                        <ol className="space-y-2 mt-2">
                            {TASKS.map((t, i) => {
                                const isDone = !!done[t.id];
                                const isCurrent = !isDone && location.pathname.startsWith(t.match[0]);
                                return (
                                    <li
                                        key={t.id}
                                        data-testid={`tester-task-${t.id}`}
                                        className={`rounded-2xl border px-3 py-2.5 transition-colors ${isDone
                                            ? "bg-stage_early-light/40 border-stage_early/40"
                                            : isCurrent
                                                ? "bg-yellow-soft/70 border-yellow-soft"
                                                : "bg-white border-[#E2E8F0]"
                                            }`}
                                    >
                                        <Link
                                            to={t.to}
                                            data-testid={`tester-task-link-${t.id}`}
                                            className="flex items-start gap-2.5 group"
                                        >
                                            {isDone ? (
                                                <CheckCircle2
                                                    size={18}
                                                    strokeWidth={1.8}
                                                    className="text-stage_early-dark shrink-0 mt-0.5"
                                                />
                                            ) : (
                                                <Circle
                                                    size={18}
                                                    strokeWidth={1.6}
                                                    className="text-[#6E7A74] shrink-0 mt-0.5"
                                                />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2">
                                                    <p className={`text-sm font-medium leading-snug ${isDone ? "text-stage_early-dark" : "text-[#1F2624]"}`}>
                                                        <span className="opacity-50 mr-1">{i + 1}.</span>
                                                        {t.label}
                                                    </p>
                                                    <span
                                                        data-testid={`tester-task-time-${t.id}`}
                                                        className="text-[10px] uppercase tracking-wider font-semibold text-[#6E7A74] tabular-nums shrink-0 mt-0.5"
                                                    >
                                                        {t.time}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-[#4C5551] mt-0.5 leading-snug">{t.hint}</p>
                                            </div>
                                            {!isDone && (
                                                <ArrowRight size={14} strokeWidth={1.8} className="text-[#6E7A74] group-hover:text-teal-dark mt-1 shrink-0" />
                                            )}
                                        </Link>
                                    </li>
                                );
                            })}
                        </ol>

                        {completed === total && (
                            <div
                                data-testid="tester-task-complete-banner"
                                className="mt-3 rounded-2xl bg-stage_early-light p-3 text-center"
                            >
                                <p className="text-sm font-heading font-medium text-stage_early-dark">
                                    All done. Thank you.
                                </p>
                                <p className="text-xs text-[#4C5551] mt-1">
                                    Click <strong>Finish study</strong> in the top bar to wrap up.
                                </p>
                            </div>
                        )}

                        {/* Persistent "Skip to survey" shortcut — visible
                            whenever the tester has explored at least one
                            section but hasn't filed feedback yet. Lets a
                            5-min tester wrap up without scrolling for the
                            feedback task. Hidden once feedback is done so
                            it doesn't compete with the "All done" banner. */}
                        {completed > 0 && completed < total && !hasFeedbackSubmitted(iid) && (
                            <div
                                className="mt-3 pt-3 border-t border-cream-dark/60 flex items-center justify-between gap-2"
                                data-testid="tester-task-shortcut"
                            >
                                <p className="text-[11px] text-[#6E7A74] leading-snug">
                                    Short on time? Your impressions so far are enough.
                                </p>
                                <Link
                                    to="/feedback"
                                    data-testid="tester-task-skip-to-survey"
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-teal-dark hover:underline whitespace-nowrap"
                                >
                                    <FastForward size={11} strokeWidth={2} /> Skip to survey
                                </Link>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </aside>
    );
}
